import React, { useState, useEffect } from 'react';
import BedExitDetection from './BedExitDetection';
import UserIdentification from './UserIdentification';
import DeviceControlHub, { getDeviceControlInterface } from './DeviceControlHub';
import MorningRoutineOrchestrator from './MorningRoutineOrchestrator';
import RoutineBuilder from './RoutineBuilder';
import DemoVisualizer from './DemoVisualizer';
import QuickGuide from './QuickGuide';
import ConnectedDevices from './ConnectedDevices';
import { 
  SmartDevice, 
  MorningRoutine as MorningRoutineType, 
  BedZone, 
  UserSignature,
  GaitTransition,
  StepPattern
} from './interfaces/types';

const MorningRoutine: React.FC = () => {
  // System-wide state
  const [systemEnabled, setSystemEnabled] = useState<boolean>(true);
  const [systemPaused, setSystemPaused] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<'overview' | 'devices' | 'routines'>('overview');
  
  // Devices state
  const [devices, setDevices] = useState<SmartDevice[]>([
    {
      id: 'light-bedroom',
      name: 'Bedroom Light',
      type: 'LIGHT',
      brand: 'Philips Hue',
      location: 'Bedroom',
      status: 'connected',
      lastSeen: new Date(),
      currentState: { isOn: false, brightness: 0 }
    },
    {
      id: 'blinds-bedroom',
      name: 'Bedroom Blinds',
      type: 'BLIND',
      brand: 'Lutron',
      location: 'Bedroom',
      status: 'connected',
      lastSeen: new Date(),
      currentState: { isOn: true, position: 0 }
    },
    {
      id: 'light-bathroom',
      name: 'Bathroom Light',
      type: 'LIGHT',
      brand: 'Philips Hue',
      location: 'Bathroom',
      status: 'connected',
      lastSeen: new Date(),
      currentState: { isOn: false, brightness: 0 }
    },
    {
      id: 'light-hallway',
      name: 'Hallway Light',
      type: 'LIGHT',
      brand: 'Philips Hue',
      location: 'Hallway',
      status: 'connected',
      lastSeen: new Date(),
      currentState: { isOn: false, brightness: 0 }
    },
    {
      id: 'coffee-kitchen',
      name: 'Coffee Machine',
      type: 'COFFEE_MACHINE',
      brand: 'Smart Home',
      location: 'Kitchen',
      status: 'connected',
      lastSeen: new Date(),
      currentState: { isOn: false }
    },
    {
      id: 'speaker-bedroom',
      name: 'Bedroom Speaker',
      type: 'SPEAKER',
      brand: 'Sonos',
      location: 'Bedroom',
      status: 'connected',
      lastSeen: new Date(),
      currentState: { isOn: false, volume: 0 }
    },
    {
      id: 'thermostat-home',
      name: 'Home Thermostat',
      type: 'THERMOSTAT',
      brand: 'Nest',
      location: 'Living Room',
      status: 'connected',
      lastSeen: new Date(),
      currentState: { isOn: true, temperature: 68 }
    }
  ]);
  
  // Bed zones state
  const [bedZones, setBedZones] = useState<BedZone[]>([
    {
      id: 'bed-zone-1',
      name: 'Head Area',
      pressureThreshold: 30,
      position: { x1: 0, y1: 0, x2: 50, y2: 50 }
    },
    {
      id: 'bed-zone-2',
      name: 'Body Area',
      pressureThreshold: 40,
      position: { x1: 50, y1: 0, x2: 100, y2: 100 }
    }
  ]);
  
  // User identification state
  const [knownUsers, setKnownUsers] = useState<UserSignature[]>([
    {
      userId: 'user-1',
      name: 'Emma Martinez',
      morningGaitTransitions: [createDummyGaitTransition()],
      typicalBedExitTime: [{ hour: 7, minute: 30 }],
      confidence: 90
    }
  ]);
  
  // Routines state
  const [routines, setRoutines] = useState<MorningRoutineType[]>([
    {
      id: 'routine-morning-1',
      name: 'Gentle Morning Wake-up',
      description: 'Gradually prepare for the day with personalized lighting, temperature, and music adjustments',
      enabled: true,
      steps: [
        {
          id: 'step-1',
          name: 'Pre-Wake Temperature Adjustment',
          description: 'Gradually increase room temperature for a more comfortable wake-up',
          condition: {
            type: 'TIME',
            timeWindow: { start: { hour: 6, minute: 30 }, end: { hour: 7, minute: 0 } }
          },
          actions: [{
            deviceId: 'thermostat-home',
            command: { commandType: 'SET_TEMPERATURE', value: 72 }
          }],
          isOptional: false
        },
        {
          id: 'step-2',
          name: 'Sunrise Simulation',
          description: 'Gradually increase bedroom lighting to simulate sunrise',
          condition: {
            type: 'TIME',
            timeWindow: { start: { hour: 7, minute: 0 }, end: { hour: 7, minute: 15 } }
          },
          actions: [{
            deviceId: 'light-bedroom',
            command: { commandType: 'DIM', value: 30 }
          }],
          isOptional: false
        },
        {
          id: 'step-3',
          name: 'Open Bedroom Blinds',
          description: 'Let in natural light',
          condition: {
            type: 'TIME',
            timeWindow: { start: { hour: 7, minute: 15 }, end: { hour: 7, minute: 30 } }
          },
          actions: [{
            deviceId: 'blinds-bedroom',
            command: { commandType: 'OPEN' },
            delayBefore: 10000 // 10 seconds after alarm
          }],
          isOptional: false
        },
        {
          id: 'step-4',
          name: 'Morning Music',
          description: 'Play gentle morning playlist',
          condition: {
            type: 'TIME',
            timeWindow: { start: { hour: 7, minute: 20 }, end: { hour: 7, minute: 45 } }
          },
          actions: [
            {
              deviceId: 'speaker-bedroom',
              command: { commandType: 'ON' }
            },
            {
              deviceId: 'speaker-bedroom',
              command: { commandType: 'SET_VOLUME', value: 30 },
              delayBefore: 2000
            },
            {
              deviceId: 'speaker-bedroom',
              command: { commandType: 'PLAY_MEDIA', value: 'Morning Playlist' },
              delayBefore: 4000
            }
          ],
          isOptional: true
        },
        {
          id: 'step-5',
          name: 'Coffee Preparation',
          description: 'Start brewing coffee',
          condition: {
            type: 'BED_EXIT'
          },
          actions: [{
            deviceId: 'coffee-kitchen',
            command: { commandType: 'ON' }
          }],
          isOptional: false
        },
        {
          id: 'step-6',
          name: 'Path Lighting',
          description: 'Turn on hallway lights when motion detected',
          condition: {
            type: 'MOTION'
          },
          actions: [{
            deviceId: 'light-hallway',
            command: { commandType: 'ON' }
          }],
          isOptional: true
        },
        {
          id: 'step-7',
          name: 'Bathroom Lighting',
          description: 'Turn on bathroom lights when presence detected',
          condition: {
            type: 'PRESENCE'
          },
          actions: [{
            deviceId: 'light-bathroom',
            command: { commandType: 'ON' }
          }],
          isOptional: true
        }
      ],
      defaultStartTime: { hour: 6, minute: 30 },
      daysOfWeek: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
      creator: 'User',
      createdAt: new Date(Date.now() - 86400000), // Yesterday
      lastModified: new Date(Date.now() - 86400000), // Yesterday
    }
  ]);
  
  // Event handlers
  const handleDeviceUpdate = (updatedDevice: SmartDevice) => {
    setDevices(prevDevices => 
      prevDevices.map(device => 
        device.id === updatedDevice.id ? updatedDevice : device
      )
    );
  };
  
  const handleDeviceError = (error: string, deviceId: string) => {
    console.error(`Device error (${deviceId}): ${error}`);
    // In a real app, we might display a notification or log this in a more persistent way
  };
  
  const handleBedExitDetected = (confidence: number) => {
    console.log(`Bed exit detected with ${confidence}% confidence`);
    // In a real app, this would trigger routines or other actions
  };
  
  const handleFalseAlarm = () => {
    console.log('Bed exit false alarm detected');
  };
  
  const handleUserIdentified = (userId: string, confidence: number) => {
    console.log(`User identified: ${userId} with ${confidence}% confidence`);
    // In a real app, this would be used to customize routines or other actions
  };
  
  const handleRoutineStart = (routineId: string) => {
    console.log(`Routine started: ${routineId}`);
  };
  
  const handleRoutineComplete = (routineId: string, success: boolean) => {
    console.log(`Routine ${routineId} completed with ${success ? 'success' : 'failure'}`);
  };
  
  const handleStepComplete = (routineId: string, stepId: string, success: boolean) => {
    console.log(`Step ${stepId} of routine ${routineId} completed with ${success ? 'success' : 'failure'}`);
  };
  
  const handleSaveRoutine = (routine: MorningRoutineType) => {
    // Check if this is an update or a new routine
    const exists = routines.some(r => r.id === routine.id);
    
    if (exists) {
      // Update existing routine
      setRoutines(prevRoutines => 
        prevRoutines.map(r => r.id === routine.id ? routine : r)
      );
    } else {
      // Add new routine
      setRoutines(prevRoutines => [...prevRoutines, routine]);
    }
  };
  
  const handleDeleteRoutine = (routineId: string) => {
    setRoutines(prevRoutines => prevRoutines.filter(r => r.id !== routineId));
  };
  
  const handleTestRoutine = (routineId: string) => {
    console.log(`Testing routine: ${routineId}`);
    // In a real app, this would trigger a test run of the routine
  };
  
  // Add new handler for adding devices
  const handleAddDevice = (newDevice: SmartDevice) => {
    // Simulate a delay for "pairing" the device
    setTimeout(() => {
      // Update the device to be connected after a brief delay
      const connectedDevice = {
        ...newDevice,
        status: 'connected' as const
      };
      
      setDevices(prevDevices => [...prevDevices, connectedDevice]);
    }, 2000);
    
    // First add the device in pairing state
    setDevices(prevDevices => [...prevDevices, newDevice]);
  };
  
  // Helper function to create a dummy gait transition for demo purposes
  function createDummyGaitTransition(): GaitTransition {
    const steps: StepPattern[] = [];
    
    // Create 6 steps over 3 seconds
    for (let i = 0; i < 6; i++) {
      steps.push({
        timestamp: new Date(Date.now() - (3000 - i * 500)),
        pressureMap: Array(10).fill(Array(10).fill(50)),
        centerOfPressure: { x: 50 + i, y: 50 + i },
        weightDistribution: { leftRight: 0.1, heelToe: 0.2 }
      });
    }
    
    return {
      initialSteps: steps,
      strideLength: 65,
      stepCadence: 112,
      weightDistribution: { leftRight: 0.1, heelToe: 0.2 }
    };
  }
  
  return (
    <div className="p-4">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Morning Routine Automation</h2>
        <p className="text-gray-600">Configure and monitor your smart morning automation</p>
      </div>
      
      {/* System Controls */}
      <div className="flex justify-between items-center mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex space-x-4">
          <button
            onClick={() => setSystemEnabled(prev => !prev)}
            className={`px-4 py-2 rounded-lg ${
              systemEnabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}
          >
            {systemEnabled ? 'System Enabled' : 'System Disabled'}
          </button>
          
          {systemEnabled && (
            <button
              onClick={() => setSystemPaused(prev => !prev)}
              className={`px-4 py-2 rounded-lg ${
                systemPaused ? 'bg-yellow-500 text-white' : 'bg-blue-500 text-white'
              }`}
            >
              {systemPaused ? 'Resume System' : 'Pause System'}
            </button>
          )}
        </div>
        
        <div className="flex border rounded-lg overflow-hidden">
          <button
            onClick={() => setCurrentTab('overview')}
            className={`px-4 py-2 ${
              currentTab === 'overview' ? 'bg-blue-500 text-white' : 'bg-gray-100'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setCurrentTab('devices')}
            className={`px-4 py-2 ${
              currentTab === 'devices' ? 'bg-blue-500 text-white' : 'bg-gray-100'
            }`}
          >
            Devices
          </button>
          <button
            onClick={() => setCurrentTab('routines')}
            className={`px-4 py-2 ${
              currentTab === 'routines' ? 'bg-blue-500 text-white' : 'bg-gray-100'
            }`}
          >
            Routines
          </button>
        </div>
      </div>
      
      {/* Tab Content */}
      {currentTab === 'overview' && (
        <div>
          {/* Quick Guide */}
          <QuickGuide />
          
          {/* Demo Visualizer */}
          <DemoVisualizer routines={routines} devices={devices} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <div className="space-y-6">
              <BedExitDetection
                enabled={systemEnabled && !systemPaused}
                onExitDetected={handleBedExitDetected}
                onFalseAlarm={handleFalseAlarm}
                bedZones={bedZones}
                sensitivityThreshold={75}
              />
              
              <UserIdentification
                enabled={systemEnabled && !systemPaused}
                onUserIdentified={handleUserIdentified}
                knownUsers={knownUsers}
                minimumConfidenceThreshold={70}
              />
            </div>
            
            <div>
              <MorningRoutineOrchestrator
                routines={routines}
                devices={devices}
                onRoutineStart={handleRoutineStart}
                onRoutineComplete={handleRoutineComplete}
                onStepComplete={handleStepComplete}
                paused={systemPaused}
                enabled={systemEnabled}
              />
            </div>
          </div>
        </div>
      )}
      
      {currentTab === 'devices' && (
        <div>
          <DeviceControlHub
            devices={devices}
            onDeviceUpdate={handleDeviceUpdate}
            onError={handleDeviceError}
            simulationMode={true}
          />
          <ConnectedDevices 
            devices={devices}
            onAddDevice={handleAddDevice}
          />
        </div>
      )}
      
      {currentTab === 'routines' && (
        <div>
          <RoutineBuilder
            devices={devices}
            existingRoutines={routines}
            onSaveRoutine={handleSaveRoutine}
            onDeleteRoutine={handleDeleteRoutine}
            onTestRoutine={handleTestRoutine}
          />
        </div>
      )}
    </div>
  );
};

export default MorningRoutine; 